import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { authTokenRefresh } from '../lib/apis/v1/auth.service';
import { useLoginStore } from '../store/useLoginStore';

interface FailedRequest {
  resolve: (token: string) => void;
  reject: (error: any) => void;
}

let isRefreshing = false;
let failedQueue: FailedRequest[] = [];

// 큐에 있는 요청들을 처리하거나 거절하는 헬퍼 함수
const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  failedQueue = [];
};

export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

apiClient.interceptors.request.use(
  (config) => {
    let token = "";

    try {
      const access_items = localStorage.getItem("loginInfo");
      if (access_items) {
        const access = JSON.parse(access_items);
        token = access.state?.access?.accessToken;
      }
    } catch (error) {
      console.error("Failed to parse auth token form storage:", error);
    }


    if (config.headers) {
      if (token && !config.url?.includes("/v1/auth/getAccessToken")) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return Promise.resolve(config)
  },
  (error) => {
    return Promise.reject(error)
  },
)

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // 1. 401 에러이고, 재시도한 적이 없는 요청일 때
    if (error.response?.status === 401 && !originalRequest._retry) {

      // 이미 토큰 갱신 중이라면 큐에 추가하고 대기
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers['Authorization'] = `Bearer ${token}`;
            return apiClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true; // 무한 루프 방지용 플래그
      isRefreshing = true;

      try {
        let refreshToken = "";
        const access_items = localStorage.getItem("loginInfo");
        if (access_items) {
          const access = JSON.parse(access_items);
          refreshToken = access.state?.access?.refreshToken;
        }
        const data = await authTokenRefresh(refreshToken);

        const newAccessToken = data.result.accessToken;
        const originAccess = useLoginStore.getState().access;
        useLoginStore.getState().setAccess({ ...originAccess, accessToken: newAccessToken, refreshToken: data.result.refreshToken });

        // 3. 큐에 쌓인 모든 요청 재실행
        processQueue(null, newAccessToken);

        // 4. 원래 실패했던 요청 재실행
        originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
        return apiClient(originalRequest);

      } catch (refreshError) {
        // 5. Refresh Token도 만료되었거나 실패한 경우
        processQueue(refreshError, null);

        const originAccess = useLoginStore.getState().access;
        useLoginStore.getState().setAccess({ ...originAccess, accessToken: "", refreshToken: "" });
        localStorage.removeItem("loginInfo");
        window.location.href = "/login";
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);